import React, { useEffect, useState } from 'react';
import LoadError from '../../components/LoadError';
import { apiGet, apiPut, apiPost, apiDelete } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { UserCircle, Users, Ban, CheckCircle, KeyRound, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AllPartners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const fetchPartners = () => {
    setLoadError(false);
    apiGet('/admin/partners').then(r => setPartners(r.data)).catch(() => setLoadError(true)).finally(() => setLoading(false));
  };

  useEffect(() => { fetchPartners(); }, []);

  // One action per partner at a time; every action asks first because each one e-mails or is permanent.
  const [busyId, setBusyId] = useState(null);
  const run = async (partner, question, request, success) => {
    if (busyId || (question && !window.confirm(question))) return;
    setBusyId(partner._id);
    try {
      const { data } = await request();
      toast.success(success(data));
      fetchPartners();
    } catch (err) { toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.'); }
    finally { setBusyId(null); }
  };
  const label = (p) => p.name || p.email;
  const toggleActive = (p) => run(p,
    p.is_active !== false ? `Disable ${label(p)}? They will no longer be able to sign in.` : null,
    () => apiPut(`/admin/partners/${p._id}/toggle-active`),
    (data) => data.is_active ? 'Partner activated' : 'Partner deactivated');
  const sendNewPassword = (p) => run(p,
    `Send ${label(p)} a new password? Their current password stops working, and the new one is e-mailed to ${p.email}.`,
    () => apiPost(`/admin/partners/${p._id}/send-new-password`, {}),
    () => `New password e-mailed to ${p.email}`);
  const deletePartner = (p) => run(p,
    `Permanently delete ${label(p)} (${p.email})? This cannot be undone.`,
    () => apiDelete(`/admin/partners/${p._id}`),
    () => `${label(p)} deleted`);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-3 border-brand border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="all-partners-page">
      <div>
        <h1 className="text-3xl font-semibold text-ember tracking-tight" style={{ fontFamily: 'Outfit' }}>Partners</h1>
        <p className="text-ink-2 mt-1">All registered partner organizations</p>
      </div>

      {loadError ? (
        <LoadError what="the partner list" onRetry={() => { setLoading(true); fetchPartners(); }} />
      ) : partners.length === 0 ? (
        <div className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 p-12 text-center">
          <UserCircle className="w-12 h-12 text-ink-3 mx-auto mb-4" />
          <p className="text-ink-2">No partners registered yet</p>
        </div>
      ) : (
        <div className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="partners-table">
              <thead>
                <tr className="bg-contrast/5 border-b border-contrast/10">
                  <th className="text-left py-3 px-4 font-medium text-ink-2">Partner</th>
                  <th className="text-left py-3 px-4 font-medium text-ink-2">Organization</th>
                  <th className="text-left py-3 px-4 font-medium text-ink-2">Email</th>
                  <th className="text-left py-3 px-4 font-medium text-ink-2">Phone</th>
                  <th className="text-center py-3 px-4 font-medium text-ink-2">Candidates</th>
                  <th className="text-center py-3 px-4 font-medium text-ink-2">Status</th>
                  <th className="text-right py-3 px-4 font-medium text-ink-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {partners.map((p) => (
                  <tr key={p._id} className="border-b border-contrast/[0.07] hover:bg-contrast/5" data-testid={`partner-row-${p._id}`}>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-surface-3 rounded-full flex items-center justify-center text-sm font-medium text-brand">
                          {p.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <span className="font-medium text-ink">{p.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-ink-2">{p.organization || 'N/A'}</td>
                    <td className="py-3 px-4 text-ink-2">{p.email}</td>
                    <td className="py-3 px-4 text-ink-2 whitespace-nowrap">{p.phone || 'N/A'}</td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-contrast/[0.07] text-ink rounded-full text-xs font-medium">
                        <Users className="w-3 h-3" /> {p.candidate_count}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${p.is_active !== false ? 'bg-brand/10 text-success' : 'bg-ember/10 text-danger'}`}>
                        {p.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleActive(p)}
                          disabled={busyId === p._id}
                          data-testid={`toggle-partner-${p._id}`}
                          className={p.is_active !== false ? 'text-ember hover:text-danger hover:bg-ember/10' : 'text-success hover:text-success hover:bg-brand/10'}
                        >
                          {p.is_active !== false ? <><Ban className="w-4 h-4 mr-1" /> Disable</> : <><CheckCircle className="w-4 h-4 mr-1" /> Enable</>}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => sendNewPassword(p)}
                          disabled={busyId === p._id}
                          data-testid={`send-password-${p._id}`}
                          className="text-brand hover:text-brand-soft hover:bg-surface-3"
                        >
                          <KeyRound className="w-4 h-4 mr-1" /> Send new password
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deletePartner(p)}
                          disabled={busyId === p._id}
                          data-testid={`delete-partner-${p._id}`}
                          title={p.candidate_count ? 'Partners with students can only be disabled' : undefined}
                          className="text-ember hover:text-danger hover:bg-ember/10"
                        >
                          <Trash2 className="w-4 h-4 mr-1" /> Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
